import React, { useState } from 'react';
import './App.css';
import CameraCapture from './components/CameraCapture';
import ImageUpload from './components/ImageUpload';
import { generateCartoonAvatar, analyzeImageStyle } from './services/api';

function App() {
  const [apiKey, setApiKey] = useState('');
  const [isKeySet, setIsKeySet] = useState(false);
  const [modelType, setModelType] = useState('flash'); // 'flash' | 'pro'

  const [styleImage, setStyleImage] = useState(null);
  const [subjectImages, setSubjectImages] = useState([]); // Array of strings
  const [stylePrompt, setStylePrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  
  const [activeInput, setActiveInput] = useState(null); // 'style' | 'subject' | null
  const [inputMode, setInputMode] = useState(null); // 'camera' | 'upload' | null

  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [usageStats, setUsageStats] = useState(null);
  const [lastRunMeta, setLastRunMeta] = useState(null); // { model: 'flash'|'pro', type: 'image'|'text' }
  const [sessionTotalCost, setSessionTotalCost] = useState(0);

  const handleImageSelect = (imgSrc) => {
    if (activeInput === 'style') {
      setStyleImage(imgSrc);
      // Optional: Auto-analyze style when image is picked?
      // For now, let's keep it manual or on-generate to save tokens
    } else if (activeInput === 'subject') {
      setSubjectImages(prev => [...prev, imgSrc]);
    }
    closeInput();
  };

  const openInput = (target, mode) => {
    setActiveInput(target);
    setInputMode(mode);
  };

  const closeInput = () => {
    setActiveInput(null);
    setInputMode(null);
  };

  const getModelName = (type) => {
    // Return model names for generation. For analysis we might use slightly different ones if needed, 
    // but usually the main model handles both or we map them.
    // Flash: gemini-2.5-flash-image (Generation), gemini-2.5-flash (Analysis - inferred in api.js if we pass same?)
    // Actually api.js defaults are distinct. Let's pass the specific model name.
    if (type === 'pro') return 'gemini-3-pro-image-preview'; 
    return 'gemini-2.5-flash-image'; 
  };
  
  const getAnalysisModelName = (type) => {
      // Use gemini-2.5-flash for everything else or if user insists 'gemini-3-pro-image-preview' handles everything.
      // Since user said 1.5 pro doesn't exist, we fallback to flash or 3-pro.
      // Let's use 3-pro for pro analysis too, as it likely supports it.
      if (type === 'pro') return 'gemini-3-pro-image-preview';
      return 'gemini-2.5-flash';
  };

  const calculateCost = (usage, meta) => {
    if (!usage || !meta) return 0;
    const { promptTokenCount = 0, candidatesTokenCount = 0 } = usage;
    let inputPrice = 0;
    let outputPrice = 0;

    if (meta.model === 'pro') {
      inputPrice = (promptTokenCount / 1000000) * 2.00;
      if (meta.type === 'image') {
        outputPrice = (candidatesTokenCount / 1000000) * 120.00;
      } else {
        outputPrice = (candidatesTokenCount / 1000000) * 12.00;
      }
    } else {
      // Flash Model (Paid Tier)
      // Input: $0.30 / 1M (text/image)
      // Output: $2.50 / 1M
      inputPrice = (promptTokenCount / 1000000) * 0.30;
      outputPrice = (candidatesTokenCount / 1000000) * 2.50;
    }
    return inputPrice + outputPrice;
  };

  const handleAnalyzeStyle = async () => {
    if (!styleImage) return;
    setLoading(true);
    setStatusMessage('Analyzing style...');
    try {
      const result = await analyzeImageStyle(styleImage, apiKey, getAnalysisModelName(modelType));
      setStylePrompt(result.styleDescription);
      setUsageStats(result.usageMetadata);
      
      const meta = { model: modelType, type: 'text' };
      setLastRunMeta(meta);
      setSessionTotalCost(prev => prev + calculateCost(result.usageMetadata, meta));

    } catch (error) {
      alert("Failed to analyze image style.");
    } finally {
      setLoading(false);
      setStatusMessage('');
    }
  };

  const handleGenerate = async () => {
    if (subjectImages.length === 0) {
      alert("Please upload at least one subject image.");
      return;
    }

    setLoading(true);
    setGeneratedImage(null);
    setStatusMessage('Preparing...');
    
    let currentStylePrompt = stylePrompt;

    try {
      // 1. If we have a style image but no prompt, analyze first
      if (!currentStylePrompt && styleImage) {
        setStatusMessage('Analyzing style from reference image...');
        const analyzeResult = await analyzeImageStyle(styleImage, apiKey, getAnalysisModelName(modelType));
        currentStylePrompt = analyzeResult.styleDescription;
        setStylePrompt(currentStylePrompt);
        setUsageStats((prev) => ({ ...prev, ...analyzeResult.usageMetadata }));
        
        const analysisMeta = { model: modelType, type: 'text' };
        setLastRunMeta(analysisMeta);
        setSessionTotalCost(prev => prev + calculateCost(analyzeResult.usageMetadata, analysisMeta));
      }

      // 2. Generate the cartoon
      setStatusMessage('Generating cartoon avatar...');
      const result = await generateCartoonAvatar(subjectImages, currentStylePrompt, apiKey, getModelName(modelType));
      setGeneratedImage(result.cartoonUrl);
      
      // Update stats (merge with analysis stats if any)
      setUsageStats((prev) => {
        if (!prev) return result.usageMetadata;
        return {
          promptTokenCount: (prev.promptTokenCount || 0) + (result.usageMetadata.promptTokenCount || 0),
          candidatesTokenCount: (prev.candidatesTokenCount || 0) + (result.usageMetadata.candidatesTokenCount || 0),
          totalTokenCount: (prev.totalTokenCount || 0) + (result.usageMetadata.totalTokenCount || 0),
        };
      });
      
      const genMeta = { model: modelType, type: 'image' };
      setLastRunMeta(genMeta);
      setSessionTotalCost(prev => prev + calculateCost(result.usageMetadata, genMeta));

    } catch (error) {
      console.error(error);
      alert("Failed to generate cartoon. Check console for details.");
    } finally {
      setLoading(false);
      setStatusMessage('');
    }
  };

  const handleReset = () => {
    setGeneratedImage(null);
    setUsageStats(null);
    setLastRunMeta(null);
    setStatusMessage('');
  };

  const handleFullReset = () => {
    setStyleImage(null);
    setSubjectImages([]);
    setStylePrompt('');
    setGeneratedImage(null);
    setUsageStats(null);
    setLastRunMeta(null);
    setSessionTotalCost(0);
  };

  const TokenStats = ({ usage, meta }) => {
    if (!usage) return null;
    const { promptTokenCount = 0, candidatesTokenCount = 0, totalTokenCount = 0 } = usage;
    
    // Calculate cost for THIS specific display (Last Run)
    const runCost = calculateCost(usage, meta);

    return (
      <div className="token-stats">
        <h4>Last Run Usage ({meta?.model === 'pro' ? 'Pro' : 'Flash'})</h4>
        <div className="stat-row"><span>Input:</span> <span>{promptTokenCount}</span></div>
        <div className="stat-row"><span>Output:</span> <span>{candidatesTokenCount}</span></div>
        <div className="stat-row"><span>Total:</span> <span>{totalTokenCount}</span></div>
        <div className="stat-row price"><span>Run Cost:</span> <span>${runCost.toFixed(6)}</span></div>
        <div className="stat-row price total"><span>Session Total:</span> <span>${sessionTotalCost.toFixed(6)}</span></div>
      </div>
    );
  };

  return (
    <div className="App">
      <header className="App-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Cartoon Avatar Generator</h1>
        {isKeySet && (
          <div className="settings-controls">
             <select 
               value={modelType} 
               onChange={(e) => setModelType(e.target.value)}
               style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', backgroundColor: '#333', color: 'white', cursor: 'pointer' }}
             >
               <option value="flash">⚡ Flash (Fast)</option>
               <option value="pro">🧠 Pro (High Quality)</option>
             </select>
          </div>
        )}
      </header>
      
      <main className="App-main">
        {!isKeySet ? (
          <div className="api-key-screen" style={{ marginTop: '50px', maxWidth: '500px', margin: '50px auto' }}>
             <h2>Enter Gemini API Key</h2>
             <p>This app requires a Google Gemini API Key to function. The key is not saved and will be lost when you refresh.</p>
             <input 
               type="password" 
               placeholder="Paste API Key here" 
               value={apiKey}
               onChange={(e) => setApiKey(e.target.value)}
               style={{ width: '100%', padding: '10px', fontSize: '16px', marginBottom: '15px', boxSizing: 'border-box' }}
             />
             <button 
               onClick={() => apiKey.trim() && setIsKeySet(true)}
               style={{ padding: '10px 20px', fontSize: '16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '5px' }}
               disabled={!apiKey.trim()}
             >
               Start App
             </button>
             <p style={{ fontSize: '12px', color: '#666', marginTop: '20px' }}>
               Get a key at <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer">Google AI Studio</a>
             </p>
          </div>
        ) : (
          <>
        {/* Input Overlay */}
        {inputMode && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>{activeInput === 'style' ? 'Choose Style Reference' : 'Choose Subject Image'}</h3>
              {inputMode === 'camera' && (
                <CameraCapture onCapture={handleImageSelect} />
              )}
              {inputMode === 'upload' && (
                <ImageUpload onUpload={handleImageSelect} />
              )}
              <button className="close-btn" onClick={closeInput}>Cancel</button>
            </div>
          </div>
        )}

        <div className="workspace">
          {/* Left Column: Style */}
          <section className="column style-column">
            <h2>1. Style Reference</h2>
            <div className="image-preview-container">
              {styleImage ? (
                <div className="preview-box">
                  <img src={styleImage} alt="Style Ref" />
                  <button className="remove-btn" onClick={() => { setStyleImage(null); setStylePrompt(''); }}>×</button>
                </div>
              ) : (
                <div className="placeholder-box">
                  <p>Upload an image to copy its style (Optional)</p>
                  <div className="button-group">
                    <button onClick={() => openInput('style', 'camera')}>📷 Camera</button>
                    <button onClick={() => openInput('style', 'upload')}>📁 Upload</button>
                  </div>
                </div>
              )}
            </div>

            <div className="style-controls">
               <label>Style Prompt:</label>
               <textarea 
                 value={stylePrompt} 
                 onChange={(e) => setStylePrompt(e.target.value)}
                 placeholder="Enter a prompt description here, or upload an image above and click 'Analyze Style'."
               />
               <div className="mini-actions">
                 <button 
                   onClick={handleAnalyzeStyle} 
                   disabled={!styleImage || loading}
                   className="secondary-btn"
                 >
                   🪄 Analyze Image Style
                 </button>
                 <button onClick={() => setStylePrompt('')} className="text-btn">Clear</button>
               </div>
            </div>
          </section>

          {/* Right Column: Subject & Result */}
          <section className="column subject-column">
            <h2>2. Subject & Result</h2>
            <div className="image-preview-container" style={{ flexDirection: 'row', flexWrap: 'wrap', gap: '10px', alignItems: 'center', alignContent: 'center' }}>
               {subjectImages.length > 0 ? (
                 <>
                   {subjectImages.map((img, index) => (
                    <div className="preview-box" key={index} style={{ width: '100px', height: '100px' }}>
                      <img src={img} alt={`Subject ${index + 1}`} style={{ height: '100%', objectFit: 'cover' }} />
                      <button className="remove-btn" onClick={() => setSubjectImages(prev => prev.filter((_, i) => i !== index))}>×</button>
                    </div>
                   ))}
                   <button 
                     className="add-more-btn" 
                     onClick={() => openInput('subject', 'upload')}
                     style={{ width: '100px', height: '100px', border: '2px dashed #ccc', background: 'none', cursor: 'pointer', fontSize: '24px', color: '#ccc' }}
                   >
                     +
                   </button>
                   <button 
                     className="add-more-btn" 
                     onClick={() => openInput('subject', 'camera')}
                     style={{ width: '100px', height: '100px', border: '2px dashed #ccc', background: 'none', cursor: 'pointer', fontSize: '24px', color: '#ccc' }}
                   >
                     📷
                   </button>
                 </>
              ) : (
                <div className="placeholder-box">
                  <p>Upload the person(s) to cartoonize</p>
                  <div className="button-group">
                    <button onClick={() => openInput('subject', 'camera')}>📷 Camera</button>
                    <button onClick={() => openInput('subject', 'upload')}>📁 Upload</button>
                  </div>
                </div>
              )}
            </div>

            <div className="action-area">
               <button 
                 className="generate-btn" 
                 onClick={handleGenerate}
                 disabled={subjectImages.length === 0 || loading}
               >
                 {loading ? statusMessage : '🚀 Generate Cartoon'}
               </button>
            </div>

            {generatedImage && (
              <div className="result-container">
                <h3>Result</h3>
                <img src={generatedImage} alt="Generated Cartoon" className="result-image" />
                <div className="result-actions">
                  <a href={generatedImage} download="cartoon-avatar.png" className="download-btn">Download</a>
                  <button onClick={handleReset} className="secondary-btn">Try Another Style</button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Stats Footer */}
        {usageStats && <TokenStats usage={usageStats} meta={lastRunMeta} />}
        </>
        )}
      </main>
    </div>
  );
}

export default App;
